import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { replace } from 'redux-first-history';

import config from '@/config';
import { logger } from '@/config/logger';
import { oAuthErrorMessages } from '@/modules/auth/errors/oauth';
import { displayErrorMessage } from '@/modules/messages/services/actions';
import { Link } from '@/modules/ui';
import { ErrorCode, hasExistingEnterpriseInstallation, isApiAntonioError } from '@/services/utilities/isApiError';

import { signInWithOAuthResult } from '../../../services/oauth/signInWithOAuthResult';

export function useHandleOAuthResult() {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery({
        queryKey: ['handleOAuthResult', location.search],
        queryFn: async () => {
            try {
                await signInWithOAuthResult(location.search);

                return null;
            } catch (error) {
                if (isApiAntonioError(error)) {
                    if (hasExistingEnterpriseInstallation(error)) {
                        return navigate(config.routes.existingInstallation, { state: { appUrl: error.data.errorData.appUrl } });
                    }

                    if (error.data.errorCode === ErrorCode.AUTH_CLIENT_IS_NOT_CONFIGURED) {
                        dispatch(replace(`${config.routes.home}?errorCode=${ErrorCode.AUTH_CLIENT_IS_NOT_CONFIGURED}`));
                        throw error;
                    }

                    const errorMessageId = oAuthErrorMessages[error.data.errorCode as keyof typeof oAuthErrorMessages];

                    if (errorMessageId) {
                        const values =
                            error.data.errorCode === ErrorCode.USER_NOT_A_MEMBER_OF_A_WORKSPACE
                                ? {
                                      marketplaceLink: (chunks: ReactNode) => (
                                          <Link href={config.links.googleWorkspaceMarketplace} target='_blank' type='native' inheritColor>
                                              {chunks}
                                          </Link>
                                      ),
                                  }
                                : undefined;

                        dispatch(
                            displayErrorMessage({
                                message: { id: errorMessageId, values },
                                options: { duration: 20 },
                            }),
                        );
                    }
                }

                logger.error(error);

                dispatch(replace(config.routes.home));
                throw error;
            }
        },
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !!location.search,
    });
}
